import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/clients/register.css";
import { showToast } from '../../utils/toastUtils';
import "react-toastify/dist/ReactToastify.css";
import { ReactComponent as LogoSvg } from "../../assets/icons/Logo.svg";
import { CircleChevronLeft, Eye, EyeOff, HelpCircle } from "lucide-react";
import { AuthService } from "../../services/auth";
import { TermsService } from "../../services/termsService";
import { toAbsoluteUrl } from "../../config/api";

const Register: React.FC = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [confirmCode, setConfirmCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsContent, setTermsContent] = useState<{
    title?: string;
    content?: string;
    fileUrl?: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    if (!fullName.trim()) {
      showToast.error("Họ và tên không được để trống!");
      return false;
    }
    if (!/^[a-zA-ZÀ-ỹ\s]+$/u.test(fullName)) {
      showToast.error("Họ và tên chỉ được chứa chữ cái và khoảng trắng!");
      return false;
    }
    if (!email.trim()) {
      showToast.error("Email không được để trống!");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      showToast.error("Email không hợp lệ!");
      return false;
    }
    if (password.length < 6) {
      showToast.error("Mật khẩu phải có ít nhất 6 ký tự!");
      return false;
    }
    if (password !== confirmPassword) {
      showToast.error("Mật khẩu và xác nhận mật khẩu không khớp!");
      return false;
    }
    if (!/^\d{10}$/.test(phone)) {
      showToast.error("Số điện thoại phải là 10 chữ số!");
      return false;
    }
    // Giới tính và ngày sinh không bắt buộc
    if (!/^\d{6}$/.test(confirmCode)) {
      showToast.error("Mã xác nhận phải là 6 chữ số!");
      return false;
    }
    if (!agreeTerms) {
      showToast.error("Vui lòng chấp nhận điều khoản!");
      return false;
    }
    return true;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      await AuthService.register({
        fullName,
        email,
        password,
        confirmPassword,
        verificationCode: confirmCode,
        phone: phone || undefined,
        gender: gender as "male" | "female" | "other" | undefined,
        dateOfBirth: birthDate || undefined,
        termsAccepted: agreeTerms,
      });

      showToast.success(
        "Đã đăng ký thành công, vui lòng kiểm tra email để xác nhận tài khoản."
      );
      setTimeout(() => {
        navigate("/dang-nhap");
      }, 1200);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Đăng ký thất bại! Vui lòng thử lại.";
      showToast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-left">
        <img
          src={require("../../assets/images/image-login.png")}
          alt="illustration"
          className="illustration-full"
        />
      </div>
      <div className="register-right">
        <div className="back-home-link-wrapper">
          <button
            type="button"
            className="back-home-link"
            onClick={() => navigate("/dang-nhap")}
          >
            <CircleChevronLeft size={18} className="back-home-link-icon" />
            <span className="back-home-link-text">Quay lại đăng nhập</span>
          </button>
        </div>
        <div className="register-logo">
          <LogoSvg
            style={{ width: "300", height: "100", marginBottom: "2rem" }}
          />
        </div>
        <h1 className="register-title">Đăng ký tài khoản</h1>
        <form onSubmit={handleRegister} className="register-form">
          <div className="form-group-row">
            <div className="form-item">
              <label htmlFor="fullName">
                Họ và tên{" "}
                <span aria-hidden="true" style={{ color: "#e11d48" }}>
                  *
                </span>
              </label>
              <input
                id="fullName"
                type="text"
                placeholder="Nhập họ và tên"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="form-item">
              <label htmlFor="email">
                Email{" "}
                <span aria-hidden="true" style={{ color: "#e11d48" }}>
                  *
                </span>
              </label>
              <input
                id="email"
                type="email"
                placeholder="Nhập email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="form-group-row">
            <div className="form-item">
              <label htmlFor="password">
                Mật khẩu{" "}
                <span aria-hidden="true" style={{ color: "#e11d48" }}>
                  *
                </span>
              </label>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Nhập mật khẩu"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <span
                className="toggle-password-register"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
            </div>
            <div className="form-item">
              <label htmlFor="confirmPassword">
                Xác nhận mật khẩu{" "}
                <span aria-hidden="true" style={{ color: "#e11d48" }}>
                  *
                </span>
              </label>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Xác nhận mật khẩu"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <span
                className="toggle-password-register"
                onClick={() => setShowConfirmPassword((v) => !v)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
            </div>
          </div>
          <div className="form-group-row">
            <div className="form-item">
              <label htmlFor="phone">
                Số điện thoại{" "}
                <span aria-hidden="true" style={{ color: "#e11d48" }}>
                  *
                </span>
              </label>
              <input
                id="phone"
                type="tel"
                placeholder="Nhập số điện thoại"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div className="form-item">
              <label htmlFor="gender">Giới tính</label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Chọn giới tính</option>
                <option value="male">Nam</option>
                <option value="female">Nữ</option>
                <option value="other">Khác</option>
              </select>
            </div>
          </div>
          <div className="form-group-row">
            <div className="form-item">
              <label htmlFor="birthDate">Ngày tháng năm sinh</label>
              <input
                id="birthDate"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
              />
            </div>
            <div className="form-item">
              <label htmlFor="confirmCode">
                Mã xác nhận{" "}
                <span aria-hidden="true" style={{ color: "#e11d48" }}>
                  *
                </span>
                <button
                  type="button"
                  onClick={() => setIsHelpOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={isHelpOpen}
                  aria-controls="confirm-code-help"
                  title="Bấm vào để xem hướng dẫn lấy mã xác nhận"
                  style={{
                    marginLeft: 8,
                    verticalAlign: "middle",
                    display: "inline-flex",
                    alignItems: "center",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <HelpCircle size={16} />
                </button>
              </label>
              <input
                id="confirmCode"
                type="number"
                placeholder="Nhập mã xác nhận"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="terms-container">
            <label className="terms">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                required
              />
              Tôi đồng ý với các
              <button
                type="button"
                onClick={async () => {
                  try {
                    const data = await TermsService.getActiveTerms();
                    if (data) {
                      const fileUrl = data.filePath
                        ? /^https?:\/\//i.test(data.filePath)
                          ? data.filePath
                          : toAbsoluteUrl(data.filePath)
                        : undefined;
                      if (fileUrl) {
                        window.open(fileUrl, "_blank", "noopener");
                        return;
                      }
                      setTermsContent({
                        title: data.title,
                        content: data.content,
                      });
                      setIsTermsOpen(true);
                    } else {
                      setTermsContent({
                        title: "Điều khoản",
                        content: "Hiện chưa có điều khoản hoạt động.",
                      });
                      setIsTermsOpen(true);
                    }
                  } catch (err) {
                    setTermsContent({
                      title: "Điều khoản",
                      content:
                        err instanceof Error
                          ? err.message
                          : "Không thể tải điều khoản.",
                    });
                    setIsTermsOpen(true);
                  }
                }}
                aria-haspopup="dialog"
                aria-controls="terms-and-conditions"
                title="Xem các điều khoản và điều kiện"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#0a66c2",
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                  marginLeft: 4,
                }}
              >
                điều khoản và điều kiện
              </button>{" "}
              <span aria-hidden="true" style={{ color: "#e11d48" }}>
                *
              </span>
            </label>
          </div>
          <button
            className="action-btn"
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            aria-live="polite"
          >
            {isSubmitting ? "Đang đăng ký..." : "Đăng ký"}
          </button>
        </form>
        {isHelpOpen && (
          <div
            id="confirm-code-help"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-code-help-title"
            className="help-modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsHelpOpen(false);
            }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="help-modal"
              style={{
                width: "min(520px, 92vw)",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                padding: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <h3 id="confirm-code-help-title" style={{ margin: 0 }}>
                  Hướng dẫn lấy mã xác nhận
                </h3>
                <button
                  type="button"
                  onClick={() => setIsHelpOpen(false)}
                  aria-label="Đóng hướng dẫn"
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: 20,
                    cursor: "pointer",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ fontSize: 14, color: "#333" }}>
                <ol style={{ paddingLeft: 18, marginTop: 8, marginBottom: 12 }}>
                  {/* <li>Bước 1: Truy cập trang <a href="https://sdtc.vn" target="_blank" rel="noopener noreferrer">sdtc.vn</a>.</li> */}
                  <li>Bước 1: Truy cập trang sdtc.vn hoặc huyenhocviet.com.</li>
                  <li>Bước 2: Lướt tới cuối trang.</li>
                  <li>Bước 3: Sao chép 6 số cuối của Hotline.</li>
                  <li>Bước 4: Trở lại đây và dán vào ô "Mã xác nhận".</li>
                  <li>Bước 5: Nhấn "Đăng ký" để hoàn tất.</li>
                </ol>
                <p style={{ margin: 0 }}>
                  Nếu cần hỗ trợ, vui lòng liên hệ bộ phận hỗ trợ của chúng tôi.
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 16,
                  gap: 8,
                }}
              >
                {/* <a
                  href="https://sdtc.vn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-btn"
                  style={{ textDecoration: "none", padding: "10px 14px" }}
                >
                  Mở sdtc.vn
                </a> */}
                <button
                  type="button"
                  className="action-btn"
                  onClick={() => setIsHelpOpen(false)}
                >
                  Đã hiểu
                </button>
              </div>
            </div>
          </div>
        )}
        {isTermsOpen && (
          <div
            id="terms-and-conditions"
            role="dialog"
            aria-modal="true"
            aria-labelledby="terms-and-conditions-title"
            className="help-modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsTermsOpen(false);
            }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="help-modal"
              style={{
                width: "min(720px, 94vw)",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                padding: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <h3 id="terms-and-conditions-title" style={{ margin: 0 }}>
                  {termsContent?.title || "Điều khoản"}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsTermsOpen(false)}
                  aria-label="Đóng điều khoản và điều kiện"
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: 20,
                    cursor: "pointer",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                  paddingRight: 6,
                  color: "#333",
                  fontSize: 14,
                }}
              >
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "inherit",
                    margin: 0,
                  }}
                >
                  {termsContent?.content || ""}
                </pre>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 16,
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  onClick={() => setIsTermsOpen(false)}
                  className="action-btn"
                >
                  Tôi đã đọc
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
